/**
 * Renders the exact infographic or video version the Adaptive Learning Engine
 * resolved for this learner, and reports delivery/exposure once it is actually
 * shown. It never looks media up by lesson — the version comes from the
 * resolver response, so audit and effectiveness stay bound to one version.
 *
 * Renders nothing when the engine did not select a media intervention, so a
 * pending or unavailable generation silently leaves the surrounding support
 * content in place.
 */
import { useEffect, useRef } from "react";
import { BarChart3, Clapperboard } from "lucide-react";
import { useAdaptiveMediaIntervention } from "@/hooks/useAdaptiveMediaIntervention";

interface Props {
  /** Only resolve when the learning moment actually calls for support. */
  enabled?: boolean;
}

export default function AdaptiveMediaIntervention({ enabled = true }: Props) {
  const { status, intervention, recordDelivery } = useAdaptiveMediaIntervention(enabled);
  const seenRef = useRef(false);

  useEffect(() => {
    if (status === "ready" && intervention && !seenRef.current) {
      seenRef.current = true;
      void recordDelivery();
    }
  }, [status, intervention, recordDelivery]);

  if (status !== "ready" || !intervention) return null;

  const isVideo = intervention.modality === "video";

  return (
    <div className="mt-6 rounded-2xl border border-primary/20 bg-card/60 p-4 md:p-5">
      <div className="mb-3 flex items-center gap-2">
        {isVideo ? (
          <Clapperboard className="h-4 w-4 text-primary/70" />
        ) : (
          <BarChart3 className="h-4 w-4 text-primary/70" />
        )}
        <span className="font-heading text-xs uppercase tracking-widest text-muted-foreground">
          {isVideo ? "A short explanation for this idea" : "A visual for this idea"}
        </span>
      </div>

      {intervention.title && (
        <h3 className="mb-3 font-heading text-base font-bold leading-tight text-foreground md:text-lg">
          {intervention.title}
        </h3>
      )}

      {isVideo ? (
        <video
          src={intervention.mediaUrl}
          controls
          playsInline
          preload="metadata"
          className="w-full rounded-xl border border-border bg-black"
        >
          {/* Captions belong to this exact version, never to "the lesson". */}
          {intervention.captionPath && (
            <track kind="captions" src={intervention.captionPath} srcLang="en" label="English" default />
          )}
        </video>
      ) : (
        <img
          src={intervention.mediaUrl}
          alt={intervention.title ?? "Adaptive explanation visual"}
          className="w-full rounded-xl border border-border bg-muted/30 object-contain"
          loading="lazy"
        />
      )}
    </div>
  );
}
